import Helper from 'utils/Helper';
import BastionType from 'gql/BastionType';
import { IterablePaginationResult, PaginationResult, WrapPaginationResultAsIterable } from 'gql/Common';
import BaseTarget from 'gql/BaseTarget';
import { LITE_REQUEST_ACCESS } from 'gql/TargetGroup';

export default interface Customer extends BaseTarget {
  region: string;
  awsAccountId: string;
  cloudAccountId: string;
  cloudProvider: string;
  domain: string;
  status: string;
  AwsWorkspacePrefix: string;
  instanceName: string;
  clusterInstanceName: string;
  clusterId: string;
  ring: {
    id: string;
    name: string;
  };
  bastionTypes: BastionType[];
  host: string;
  nextToken?: string;
}

export enum CloudProvider {
  AWS = 'AWS',
  AWS_OCI = 'AWS_OCI',
  AZURE = 'Azure',
  GOV_AWS = 'Gov_AWS',
}

/**
 * Return the details for a customer
 */
export const CUSTOMER = `{
  id

  region
  awsAccountId
  cloudAccountId
  cloudProvider
  domain
  status
  AwsWorkspacePrefix
  instanceName
  clusterInstanceName
  clusterId
  ring {
    id 
    name
  }
  bastionTypes
  host
  nextToken

  createdBy
  createdAt
  updatedBy
  updatedAt
  name
  description
  tier
  deleted
  tags {
    key
    value
  }

  approvalSlackChannel
  notifyAutoApproval
  enablePaging

  teamRole
  managersADGroups
  orgLeaderRole
  limitedAccessRoles
  accessRequiresBastion
  accessConfiguration {
    level
    access ${LITE_REQUEST_ACCESS}
  }
}`;

export const CUSTOMER_LITE = `{
  id
  name
  region
  awsAccountId
  cloudAccountId
  cloudProvider
  status
  instanceName
  clusterInstanceName
  clusterId
  ring {
    id 
    name
  }
  host
  deleted
}`;

/**
 * Return all customers lockbox knows about
 */
const LIST_CUSTOMERS = `
    query listCustomers($filter: CustomerFilterInput, $pagination: PaginationInput) {
      listCustomers(filter: $filter, pagination: $pagination) {
        items ${CUSTOMER_LITE}
        total
        nextToken
    }
  }
`;

const GET_CUSTOMER_BY_ID = `
    query customer($id: String!) {
      customer (id: $id) ${CUSTOMER}
    }
`;

export interface ListCustomersOptions {
  nextToken?: string;
}

/**
 * Calls API for GET_CUSTOMERS
 * @returns Promise<Customer>
 */
export const listCustomers = async (variables?: ListCustomersOptions): Promise<IterablePaginationResult<Customer>> => {
  const result = await Helper.execGQL<PaginationResult<Customer>>({
    query: LIST_CUSTOMERS,
    variables: {
      filter: {
        deleted: false,
      },
      pagination: {
        nextToken: variables?.nextToken,
      },
    },
  });
  return WrapPaginationResultAsIterable(result.listCustomers);
};

export interface GetCustomerOptions {
  id: string;
}

export const getCustomer = async (variables: GetCustomerOptions): Promise<Customer> => {
  const result = await Helper.execGQL<Customer>({
    query: GET_CUSTOMER_BY_ID,
    variables,
  });
  const { customer } = result;
  return customer;
};
